import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../../lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertCircle,
  ArrowDownUp,
  Building2,
  CheckCircle2,
  ChevronDown,
  Download,
  Loader2,
  MailWarning,
  Search,
  TriangleAlert,
  Users as UsersIcon,
  Send,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  computeCurrentStage,
  currentPeriodDueDate,
  isPaidCurrentPeriod,
  paymentStatusForStage,
  reminderActionLabel,
  reminderInternalLabel,
  reminderStatusLabel,
  renderReminderEmail,
  rentDaysOverdue,
  resolveDunningFee,
  type ReminderStage,
  type ReminderTone,
} from "@/lib/rent-reminder-templates";
import { isRentCollectionEligible } from "@/lib/rent-collection";
import { recordManualRentPaidInLedger } from "@/lib/manual-rent-paid";
import { edgeFunctionErrorMessage } from "@/lib/edge-function-error";
import { formatDateNumeric } from "@/lib/date-format";
import { effectiveRentForMonthEUR } from "@/lib/rent-discount";
import { fetchRentLedgerForBookings } from "@/lib/rent-ledger-fetch";
import {
  nextStageInChain,
  currentPeriodLedger,
  currentRowsForFeePlacement,
  daysSince,
  keepInvoicedCurrentRows,
  placeBookingFees,
  selectOpenPastMonthRent,
} from "@/lib/rent-past-months";
import { PROPERTY_REMINDER_STATS_KEY } from "@/hooks/v2/usePropertyReminderStats";
import { PROPERTY_RENT_PAID_STATS_KEY } from "@/hooks/v2/usePropertyRentPaidStats";
import { invalidateOwnerCaches } from "@/hooks/v2/owner-detail-keys";

// ───────────────────────────────────────────────────────────────────────────
// Types
// ───────────────────────────────────────────────────────────────────────────

type PaymentStatus =
  "paid" | "pending" | "overdue_1" | "overdue_2" | "overdue_3" | "inkasso";

type FilterKey =
  | "all_tenants"
  | "all_overdue"
  | "stage_1"
  | "stage_2_3"
  | "inkasso"
  | "paid_this_month"
  | "past_months";

type SortKey = "days_overdue" | "amount" | "name";

interface ReminderRow {
  booking_id: number;
  user_id: string | null;
  room_id: number | null;
  property_id: number | null;
  /**
   * What this row is CHASING — the still-open rent, in EUR (COL-417).
   *
   * Every total, sort, export, mark-paid amount and drafted reminder in this
   * view reads this one field, which is why the partial subtraction happens
   * here rather than at each of those call sites: a reminder that demands the
   * full month from a tenant who already part-paid is the single worst thing
   * this screen can do.
   */
  monthly_rent: number;
  /** What the month BILLS, before any receipt. Display only. */
  rent_billed_eur: number;
  /** Cents already received against the month, in EUR. Zero when none. */
  rent_received_eur: number;
  payment_status: PaymentStatus;
  rent_due_date: number | null;
  reminder_stage: 0 | 1 | 2 | 3 | 4;
  reminder_sent_at: string | null;
  accumulated_fees_eur: number;
  inkasso_referred_at: string | null;
  last_payment_received_at: string | null;
  // hydrated
  tenant_name: string | null;
  tenant_email: string | null;
  room_title: string | null;
  property_title: string | null;
  property_city: string | null;
  // computed
  days_overdue: number;
  current_stage: 0 | ReminderStage;
  is_confirmed_active: boolean;
  /** Whether automated reminders run for this unit (property default, apartment
   *  override). false = owner collects rent / no bank visibility → suppressed
   *  from overdue detection, counts and sends. */
  reminders_enabled: boolean;
  /** Per-property Mahngebühr in EUR (COL-308), resolved via resolveDunningFee
   *  so previews match the fee the edge function renders into sent emails. */
  fee_per_stage_eur: number;
  /** Stable list key: the booking id for the current-period row,
   *  `booking:YYYY-MM` for a past-month row (COL-435). */
  row_key: string;
  /** Which billing period this row is about. A booking has one "current" row
   *  and one "past" row per earlier month still open in the ledger. */
  period_kind: "current" | "past";
  /** Past rows only: ISO due date of the open ledger row (the month it pays for). */
  period_due_date: string | null;
  /** Concrete due date for the current invoice (move-in date for first rent). */
  invoice_due_date: string | null;
  /** Past rows only: the `payments` row backing this item. */
  ledger_payment_id: string | null;
  /** The booking's accumulated Mahngebühren (booking-level). Drafts render
   *  from this so a past-month preview matches what the edge function sends;
   *  `accumulated_fees_eur` is what THIS row displays (fees appear on one row). */
  booking_fees_eur: number;
}

/** Due date of the period a row is about: the ledger due date for a past
 *  month, otherwise the booking's current billing period. */
function rowPeriodDue(
  row: Pick<
    ReminderRow,
    "period_due_date" | "invoice_due_date" | "rent_due_date"
  >,
  today: Date = new Date()
): Date {
  if (row.period_due_date) return new Date(row.period_due_date);
  if (row.invoice_due_date) return new Date(row.invoice_due_date);
  return row.rent_due_date
    ? currentPeriodDueDate(row.rent_due_date, today)
    : today;
}

interface ActivityRow {
  id: string;
  event_type:
    "payment_received" | "reminder_sent" | "marked_paid" | "inkasso_referred";
  stage: number | null;
  amount_eur: number | null;
  email_lang: "de" | "en" | null;
  created_at: string;
  created_by: string | null;
}

interface PropertyOption {
  id: number;
  title: string;
  city: string | null;
}

// ───────────────────────────────────────────────────────────────────────────
// Formatting helpers
// ───────────────────────────────────────────────────────────────────────────

function formatEur(eur: number): string {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(eur || 0));
}

/** Past-month rows carry the year so "Due 1 Jul" can't be read as this year's. */
function formatDueDate(d: Date, withYear: boolean, language?: string): string {
  return d.toLocaleDateString(language === "de" ? "de-DE" : "en-GB", {
    day: "numeric",
    month: "short",
    ...(withYear ? { year: "numeric" } : {}),
  });
}

function monthLabelDe(d: Date): string {
  return d.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
}

function formatDateDe(d: Date): string {
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** Compact fee label for inline UI copy, e.g. "€5" or "€7.50". */
function feeLabel(eur: number): string {
  return `€${Number.isInteger(eur) ? eur : eur.toFixed(2)}`;
}

// Plain-language pill styling per tone (jargon-free on screen). Amber = late,
// rose = final notice (internally Inkasso/Stage 4), emerald = on time.
const TONE_STYLES: Record<
  ReminderTone,
  { bg: string; text: string; border: string; dot: string }
> = {
  paid: {
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    border: "border-emerald-200",
    dot: "bg-emerald-500",
  },
  late: {
    bg: "bg-amber-50",
    text: "text-amber-800",
    border: "border-amber-200",
    dot: "bg-amber-500",
  },
  final: {
    bg: "bg-rose-50",
    text: "text-rose-800",
    border: "border-rose-300",
    dot: "bg-rose-600",
  },
  pending: {
    bg: "bg-slate-50",
    text: "text-slate-700",
    border: "border-slate-200",
    dot: "bg-slate-400",
  },
  off: {
    bg: "bg-slate-50",
    text: "text-slate-500",
    border: "border-slate-200",
    dot: "bg-slate-300",
  },
};

function StatusPill({ row }: { row: ReminderRow }) {
  const { label, tone } = reminderStatusLabel({
    reminders_enabled: row.reminders_enabled,
    paid: row.payment_status === "paid",
    days_overdue: row.days_overdue,
    stage: row.current_stage,
  });
  const s = TONE_STYLES[tone];
  // Internal dunning term (Stage / Mahnung / Inkasso) kept on hover only.
  const title =
    row.reminders_enabled && tone !== "paid" && row.current_stage > 0
      ? reminderInternalLabel(row.current_stage)
      : undefined;
  return (
    <Badge className={cn("gap-1.5", s.bg, s.text, s.border)} title={title}>
      <span className={cn("inline-block h-1.5 w-1.5 rounded-full", s.dot)} />
      {label}
    </Badge>
  );
}

const PAGE_SIZE = 8;

function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ───────────────────────────────────────────────────────────────────────────
// Component
// ───────────────────────────────────────────────────────────────────────────

interface AdminRemindersProps {
  /** When set, scopes the entire view to a single property (used by the
   *  property-detail "Reminders" tab). Hides the property filter dropdown
   *  and the page header. */
  propertyIdFilter?: number;
  /** Hide the outer card chrome — used by RemindersTab so the tab content
   *  blends into the property detail page. */
  embedded?: boolean;
  /** Summary-only mode: show header + stats, hide filters/table. */
  summaryOnly?: boolean;
}

export const AdminReminders = ({
  propertyIdFilter,
  embedded = false,
  summaryOnly = false,
}: AdminRemindersProps = {}) => {
  const queryClient = useQueryClient();
  const { user, isOwnerPortalOnly } = useAuth();
  const { t, language } = useLanguage();

  // Owners get a read-only view of dunning state (stage badges, fees, days
  // overdue). Sending reminders, marking paid and toggling tenants stay
  // admin-only actions.
  const readOnly = isOwnerPortalOnly;

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<FilterKey>("all_overdue");
  const [sort, setSort] = useState<SortKey>("days_overdue");
  const [propertyFilter, setPropertyFilter] = useState<number | null>(
    propertyIdFilter ?? null
  );
  const [page, setPage] = useState(1);

  const [openRowKey, setOpenRowKey] = useState<string | null>(null);
  const [draftFor, setDraftFor] = useState<ReminderRow | null>(null);
  const [draftLang, setDraftLang] = useState<"de" | "en">("de");
  const [draftSubject, setDraftSubject] = useState("");
  const [draftHtml, setDraftHtml] = useState("");
  const [draftText, setDraftText] = useState("");
  const [markPaidFor, setMarkPaidFor] = useState<ReminderRow | null>(null);

  // ── Data ──
  const {
    data,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: [
      "admin-reminders",
      "rows",
      propertyIdFilter ?? "all",
      isOwnerPortalOnly ? `owner:${user?.id ?? "anon"}` : "staff",
    ],
    queryFn: async () => {
      const { data: bookingsData, error: bErr } = await supabase
        .from("bookings")
        .select(
          "id, user_id, room_id, monthly_rent, payment_status, rent_due_date, reminder_stage, reminder_sent_at, accumulated_fees_eur, inkasso_referred_at, last_payment_received_at, rent_paid_through, manually_marked_paid, status, start_date, end_date, admin_archived_at, actual_move_in_date, move_in_date, actual_move_out_date, move_out_date, rent_discount_mode, rent_discount_value, rent_discount_months, rent_discount_start_month"
        );
      if (bErr) throw new Error(`Could not load bookings: ${bErr.message}`);
      const bookings = (bookingsData ?? []) as unknown as Array<{
        id: number;
        user_id: string | null;
        room_id: number | null;
        monthly_rent: number | null;
        rent_discount_mode: string | null;
        rent_discount_value: number | null;
        rent_discount_months: number | null;
        rent_discount_start_month: string | null;
        payment_status: PaymentStatus | null;
        rent_due_date: number | null;
        reminder_stage: number | null;
        reminder_sent_at: string | null;
        accumulated_fees_eur: number | null;
        inkasso_referred_at: string | null;
        last_payment_received_at: string | null;
        rent_paid_through: string | null;
        manually_marked_paid: boolean | null;
        status: string | null;
        start_date: string | null;
        end_date: string | null;
        admin_archived_at: string | null;
        actual_move_in_date: string | null;
        move_in_date: string | null;
        actual_move_out_date: string | null;
        move_out_date: string | null;
      }>;
      if (!bookings.length)
        return {
          rows: [] as ReminderRow[],
          properties: [] as PropertyOption[],
        };

      const userIds = Array.from(
        new Set(bookings.map((b) => b.user_id).filter(Boolean) as string[])
      );
      const roomIds = Array.from(
        new Set(bookings.map((b) => b.room_id).filter(Boolean) as number[])
      );

      const [profilesRes, roomsRes] = await Promise.all([
        userIds.length
          ? supabase
              .from("profiles")
              .select("id, name, email")
              .in("id", userIds)
          : Promise.resolve({ data: [], error: null }),
        roomIds.length
          ? supabase
              .from("rooms")
              .select("id, title, property_id, apartment_id")
              .in("id", roomIds)
          : Promise.resolve({ data: [], error: null }),
      ]);

      const rooms = (roomsRes.data ?? []) as Array<{
        id: number;
        title: string | null;
        property_id: number | null;
        apartment_id: number | null;
      }>;
      const propertyIds = Array.from(
        new Set(rooms.map((r) => r.property_id).filter(Boolean) as number[])
      );
      const apartmentIds = Array.from(
        new Set(rooms.map((r) => r.apartment_id).filter(Boolean) as number[])
      );
      const [propertiesRes, apartmentsRes] = await Promise.all([
        propertyIds.length
          ? supabase
              .from("properties")
              .select(
                "id, title, city, rent_reminders_enabled, dunning_fee_per_stage_eur"
              )
              .in("id", propertyIds)
          : Promise.resolve({ data: [], error: null }),
        apartmentIds.length
          ? supabase
              .from("apartments")
              .select("id, rent_reminders_enabled")
              .in("id", apartmentIds)
          : Promise.resolve({ data: [], error: null }),
      ]);
      const properties = (propertiesRes.data ?? []) as Array<{
        id: number;
        title: string | null;
        city: string | null;
        rent_reminders_enabled: boolean | null;
        dunning_fee_per_stage_eur: number | string | null;
      }>;
      const apartmentRemindersById = new Map<number, boolean | null>();
      for (const a of (apartmentsRes.data ?? []) as Array<{
        id: number;
        rent_reminders_enabled: boolean | null;
      }>) {
        apartmentRemindersById.set(a.id, a.rent_reminders_enabled);
      }

      const profilesById = new Map<
        string,
        { name: string | null; email: string | null }
      >();
      for (const p of (profilesRes.data ?? []) as Array<{
        id: string;
        name: string | null;
        email: string | null;
      }>) {
        profilesById.set(p.id, { name: p.name, email: p.email });
      }
      const roomsById = new Map<
        number,
        {
          title: string | null;
          property_id: number | null;
          apartment_id: number | null;
        }
      >();
      for (const r of rooms)
        roomsById.set(r.id, {
          title: r.title,
          property_id: r.property_id,
          apartment_id: r.apartment_id,
        });
      const propsById = new Map<
        number,
        {
          title: string | null;
          city: string | null;
          rent_reminders_enabled: boolean | null;
          dunning_fee_per_stage_eur: number | string | null;
        }
      >();
      for (const p of properties)
        propsById.set(p.id, {
          title: p.title,
          city: p.city,
          rent_reminders_enabled: p.rent_reminders_enabled,
          dunning_fee_per_stage_eur: p.dunning_fee_per_stage_eur,
        });

      const nowMs = Date.now();
      let rows: ReminderRow[] = bookings.map((b) => {
        const payStatus = (b.payment_status ?? "pending") as PaymentStatus;
        const rentDueDay = b.rent_due_date ?? null;

        const room = b.room_id ? (roomsById.get(b.room_id) ?? null) : null;
        const property = room?.property_id
          ? (propsById.get(room.property_id) ?? null)
          : null;
        // Per-unit reminder suppression: apartment override wins, else the
        // property default, else on (mirrors resolveRemindersEnabled). When off
        // (owner collects rent / no bank visibility) the unit is neutralised out
        // of overdue detection — never counted, escalated or sent to — but stays
        // visible with a "Reminders off" badge instead of vanishing.
        const apartmentReminders =
          room?.apartment_id != null
            ? (apartmentRemindersById.get(room.apartment_id) ?? null)
            : null;
        const remindersEnabled =
          apartmentReminders ?? property?.rent_reminders_enabled ?? true;

        // Overdue days come purely from rent_due_date, independent of the
        // stored payment_status, zeroed when the current billing period is
        // actually covered (isPaidCurrentPeriod: paid May but not June is
        // overdue for June regardless of the DB flag) or reminders are off.
        // Shared with colivIQ Brain's late-rent roll-up (lateRentStage).
        const daysOverdue = rentDaysOverdue({
          ...b,
          reminders_enabled: remindersEnabled,
        });
        const currentStage = computeCurrentStage(daysOverdue);
        const paidCurrentPeriod = isPaidCurrentPeriod(b);
        // Derive payment_status dynamically so filters reflect actual current
        // state, not the stale DB value (only updated when a reminder is sent).
        // Suppressed units are "pending" so they count as neither overdue nor
        // paid.
        const computedPayStatus = !remindersEnabled
          ? ("pending" as PaymentStatus)
          : paidCurrentPeriod
            ? ("paid" as PaymentStatus)
            : (paymentStatusForStage(currentStage) as PaymentStatus);
        // Keep notice-period tenants in collection until moved_out.
        const is_confirmed_active = isRentCollectionEligible(
          {
            status: b.status ?? "",
            admin_archived_at: b.admin_archived_at,
            actual_move_in_date: b.actual_move_in_date,
            move_in_date: b.move_in_date,
            start_date: b.start_date,
            actual_move_out_date: b.actual_move_out_date,
            move_out_date: b.move_out_date,
            end_date: b.end_date,
          },
          new Date(nowMs)
        );
        return {
          booking_id: b.id,
          user_id: b.user_id,
          room_id: b.room_id,
          property_id: room?.property_id ?? null,
          // What this tenant owes for the current period, discount included.
          // Every total, export, mark-paid amount and drafted reminder in this
          // view reads this field, so resolving it once here keeps them all
          // consistent instead of eight separate call sites (COL-360).
          monthly_rent: effectiveRentForMonthEUR(
            b as unknown as Parameters<typeof effectiveRentForMonthEUR>[0],
            // The tenant's own billing period, not the calendar month: a due
            // day later than today rolls back to the previous month, and the
            // "Mark paid" ledger write reprices the pending row for THAT month.
            currentPeriodDueDate(rentDueDay ?? 1)
              .toISOString()
              .slice(0, 10)
          ),
          // Netted against this period's receipts once the ledger is in hand
          // (see `currentPeriodLedger` below) — the booking alone knows
          // what the month bills and nothing about what arrived.
          rent_billed_eur: effectiveRentForMonthEUR(
            b as unknown as Parameters<typeof effectiveRentForMonthEUR>[0],
            currentPeriodDueDate(rentDueDay ?? 1)
              .toISOString()
              .slice(0, 10)
          ),
          rent_received_eur: 0,
          payment_status: computedPayStatus,
          rent_due_date: rentDueDay,
          reminder_stage: (b.reminder_stage ?? 0) as 0 | 1 | 2 | 3 | 4,
          reminder_sent_at: b.reminder_sent_at,
          accumulated_fees_eur: Number(b.accumulated_fees_eur ?? 0),
          inkasso_referred_at: b.inkasso_referred_at,
          last_payment_received_at: b.last_payment_received_at,
          tenant_name: b.user_id
            ? (profilesById.get(b.user_id)?.name ?? null)
            : null,
          tenant_email: b.user_id
            ? (profilesById.get(b.user_id)?.email ?? null)
            : null,
          room_title: room?.title ?? null,
          property_title: property?.title ?? null,
          property_city: property?.city ?? null,
          days_overdue: daysOverdue,
          current_stage: currentStage,
          is_confirmed_active,
          reminders_enabled: remindersEnabled,
          fee_per_stage_eur: resolveDunningFee(
            property?.dunning_fee_per_stage_eur
          ),
          row_key: String(b.id),
          booking_fees_eur: Number(b.accumulated_fees_eur ?? 0),
          period_kind: "current",
          period_due_date: null,
          invoice_due_date: null,
          ledger_payment_id: null,
        };
      });

      if (isOwnerPortalOnly && user?.id) {
        const { data: poRows, error: poErr } = await supabase
          .from("property_owners")
          .select("property_id")
          .eq("user_id", user.id);
        if (poErr) {
          throw new Error(`Could not load owner properties: ${poErr.message}`);
        }
        const allowed = new Set(
          (poRows ?? []).map((r: { property_id: number }) =>
            Number(r.property_id)
          )
        );
        rows = rows.filter(
          (r) => r.property_id != null && allowed.has(r.property_id)
        );
      }

      // ── Past months (COL-435) ──
      // Rent from billing periods before each tenant's current one that is
      // still open in the ledger — one extra row per month, for the tenants
      // this page tracks (occupying now, reminders on). Runs after the owner
      // scoping so an owner only ever sees their own properties' rows.
      const tracked = rows.filter(
        (r) => r.is_confirmed_active && r.reminders_enabled
      );
      if (tracked.length) {
        const trackedIds = new Set(tracked.map((r) => r.booking_id));
        const { ledger, proofFor } = await fetchRentLedgerForBookings(
          supabase,
          Array.from(trackedIds)
        );
        const baseByBooking = new Map(tracked.map((r) => [r.booking_id, r]));
        const pastMonths = selectOpenPastMonthRent({
          rows: ledger,
          proofFor,
          rentDueDayByBooking: new Map(
            tracked.map((r) => [r.booking_id, r.rent_due_date])
          ),
          bookingIds: trackedIds,
          now: new Date(nowMs),
        });
        // COL-417: the current period is the one row not built from a ledger
        // row, so without this it stays the only line on the screen still
        // demanding the full rent after a partial payment. Reads the ledger
        // that was just fetched — no extra query.
        const currentNow = currentPeriodLedger(
          ledger,
          new Map(tracked.map((r) => [r.booking_id, r.rent_due_date])),
          new Date(nowMs)
        );
        rows = keepInvoicedCurrentRows(rows, currentNow, trackedIds);
        for (const r of tracked) {
          const cur = currentNow.get(r.booking_id);
          if (!cur) continue;
          r.invoice_due_date = cur.dueDate;
          if (r.payment_status !== "paid") {
            r.days_overdue = daysSince(cur.dueDate, new Date(nowMs));
            r.current_stage = computeCurrentStage(r.days_overdue);
            r.payment_status = paymentStatusForStage(
              r.current_stage
            ) as PaymentStatus;
          }
          // Billed AND received both come from the current month's ledger row.
          // Netting the ledger's received against the booking's rent is what
          // silently zeroed a row whose month had an edited `amount_cents`
          // (EUR 800 invoiced, EUR 650 booking, EUR 700 in), and over-demanded
          // on the reverse edit. Where a ledger row exists it is the invoice,
          // exactly as it already is for every past-month row below.
          const billedEur =
            cur.billedCents > 0 ? cur.billedCents / 100 : r.rent_billed_eur;
          r.rent_billed_eur = billedEur;
          r.rent_received_eur = cur.receivedCents / 100;
          r.monthly_rent = Math.max(0, billedEur - cur.receivedCents / 100);
        }
        // Place booking-level fees only after the concrete invoice date has
        // recalculated the current row's overdue state.
        const { currentRowFees, pastRowFees } = placeBookingFees(
          currentRowsForFeePlacement(tracked, currentNow),
          pastMonths
        );
        for (const r of tracked) {
          const fees = currentRowFees.get(r.booking_id);
          if (fees !== undefined) r.accumulated_fees_eur = fees;
        }
        for (const m of pastMonths) {
          const base = baseByBooking.get(m.booking_id);
          if (!base) continue;
          rows.push({
            ...base,
            row_key: `${m.booking_id}:${m.ym}`,
            period_kind: "past",
            period_due_date: m.due_date,
            invoice_due_date: null,
            ledger_payment_id: m.payment_id,
            // That month's own invoice amount, less anything received
            // against it (COL-417) — `open_eur` equals `amount_eur` for every
            // month nobody part-paid.
            monthly_rent: m.open_eur,
            rent_billed_eur: m.amount_eur,
            rent_received_eur: m.received_eur,
            accumulated_fees_eur: pastRowFees.get(m.payment_id) ?? 0,
            // Open by construction; the booking's own claim may read "paid"
            // because the CURRENT period is covered.
            payment_status: "pending",
            days_overdue: m.days_overdue,
            // Next step in the booking's dunning chain (product decision:
            // continue from the stored stage rather than recomputing from a
            // due date that is always 9+ days back).
            current_stage: nextStageInChain(base.reminder_stage),
          });
        }
      }

      const propIdsInRows = new Set(
        rows.map((r) => r.property_id).filter((id): id is number => id != null)
      );
      const propertyOptions: PropertyOption[] = properties
        .filter((p) => p != null && propIdsInRows.has(p.id))
        .map((p) => ({
          id: p.id,
          title: p.title ?? `Property #${p.id}`,
          city: p.city,
        }));
      propertyOptions.sort((a, b) =>
        (a.title || "").localeCompare(b.title || "")
      );

      return { rows, properties: propertyOptions };
    },
  });
  const rows = useMemo(() => data?.rows ?? [], [data]);
  const properties = useMemo(() => data?.properties ?? [], [data]);

  // A booking can have a current-period row and past-month rows, so the open
  // panel is keyed by row (ReminderRow.row_key); activity stays per booking.
  const openTenant = openRowKey
    ? (rows.find((r) => r.row_key === openRowKey) ?? null)
    : null;
  const openTenantBookingId = openTenant?.booking_id ?? null;

  // ── Activity log for the open tenant ──
  const { data: activity } = useQuery({
    queryKey: ["admin-reminders", "activity", openTenantBookingId],
    enabled: openTenantBookingId != null,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("payments_activity")
        .select(
          "id, event_type, stage, amount_eur, email_lang, note, email_to, email_subject, email_html, email_text, created_at, created_by"
        )
        .eq("booking_id", openTenantBookingId)
        .order("created_at", { ascending: false });
      if (error) {
        console.warn("payments_activity load failed:", error);
        return [] as ActivityRow[];
      }
      return (data ?? []) as ActivityRow[];
    },
  });

  // ── Property + active/inactive scoping ──
  const scopedRows = useMemo(() => {
    let base = rows;
    if (propertyIdFilter != null) {
      base = base.filter((r) => r.property_id === propertyIdFilter);
    } else if (propertyFilter != null) {
      base = base.filter((r) => r.property_id === propertyFilter);
    }
    base = base.filter((r) => r.is_confirmed_active);
    return base;
  }, [rows, propertyIdFilter, propertyFilter]);

  // ── Counts per filter chip (always against scopedRows) ──
  // Stage chips describe the CURRENT period's dunning cycle; past-month rows
  // count under "Overdue" (they are) and "Past months", never under a stage.
  const counts = useMemo(() => {
    const current = scopedRows.filter((r) => r.period_kind === "current");
    const all_overdue = scopedRows.filter(
      (r) => r.payment_status !== "paid" && r.current_stage > 0
    ).length;
    const stage_1 = current.filter(
      (r) => r.payment_status === "overdue_1"
    ).length;
    const stage_2_3 = current.filter(
      (r) =>
        r.payment_status === "overdue_2" || r.payment_status === "overdue_3"
    ).length;
    const inkasso = current.filter(
      (r) => r.payment_status === "inkasso"
    ).length;
    const past_months = scopedRows.filter(
      (r) => r.period_kind === "past"
    ).length;
    // "Paid this month" = last_payment_received_at within the current calendar month.
    const now = new Date();
    const monthStart = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)
    ).getTime();
    const paid_this_month = scopedRows.filter((r) => {
      if (r.payment_status !== "paid" || !r.last_payment_received_at)
        return false;
      return Date.parse(r.last_payment_received_at) >= monthStart;
    }).length;
    const all_tenants = scopedRows.length;
    return {
      all_tenants,
      all_overdue,
      stage_1,
      stage_2_3,
      inkasso,
      paid_this_month,
      past_months,
    };
  }, [scopedRows]);

  const filterPasses = (r: ReminderRow): boolean => {
    if (filter === "all_tenants") return true;
    if (filter === "all_overdue")
      return r.payment_status !== "paid" && r.current_stage > 0;
    if (filter === "past_months") return r.period_kind === "past";
    if (r.period_kind !== "current") return false;
    if (filter === "stage_1") return r.payment_status === "overdue_1";
    if (filter === "stage_2_3")
      return (
        r.payment_status === "overdue_2" || r.payment_status === "overdue_3"
      );
    if (filter === "inkasso") return r.payment_status === "inkasso";
    if (filter === "paid_this_month") {
      if (r.payment_status !== "paid" || !r.last_payment_received_at)
        return false;
      const now = new Date();
      const monthStart = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)
      ).getTime();
      return Date.parse(r.last_payment_received_at) >= monthStart;
    }
    return true;
  };

  const filteredSorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    const arr = scopedRows
      .filter((r) => filterPasses(r))
      .filter((r) => {
        if (!q) return true;
        return (
          (r.tenant_name ?? "").toLowerCase().includes(q) ||
          (r.tenant_email ?? "").toLowerCase().includes(q) ||
          (r.room_title ?? "").toLowerCase().includes(q) ||
          (r.property_title ?? "").toLowerCase().includes(q) ||
          String(r.booking_id).includes(q)
        );
      });
    arr.sort((a, b) => {
      if (sort === "name")
        return (a.tenant_name ?? "").localeCompare(b.tenant_name ?? "");
      if (sort === "amount") {
        return (
          b.monthly_rent +
          b.accumulated_fees_eur -
          (a.monthly_rent + a.accumulated_fees_eur)
        );
      }
      return b.days_overdue - a.days_overdue;
    });
    return arr;
    // filterPasses depends on filter only — included via the closure deps below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedRows, search, sort, filter]);

  const totalPages = Math.max(1, Math.ceil(filteredSorted.length / PAGE_SIZE));
  useEffect(() => setPage(1), [filter, search, sort, propertyFilter]);
  const safePage = Math.min(Math.max(1, page), totalPages);
  const pageRows = filteredSorted.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE
  );

  // ── Stats (computed from currently scoped rows, and the *filtered* list) ──
  const stats = useMemo(() => {
    const scopedBookings = scopedRows;

    const filtered = filteredSorted;
    // Overdue € includes open past months (COL-435); "tenants" stays a head
    // count, so a tenant with two open months is one tenant. Stage tallies
    // describe the current period's cycle only.
    const overdueBookings = new Set<number>();
    let overdueTotalEur = 0;
    let overdueFeesEur = 0;
    let stage1 = 0;
    let stage2 = 0;
    let stage3 = 0;
    let stage4 = 0;
    for (const r of filtered) {
      if (r.payment_status === "paid") continue;
      if (r.current_stage <= 0) continue;
      overdueBookings.add(r.booking_id);
      overdueTotalEur += r.monthly_rent + r.accumulated_fees_eur;
      overdueFeesEur += r.accumulated_fees_eur;
      if (r.period_kind !== "current") continue;
      if (r.current_stage === 1) stage1 += 1;
      else if (r.current_stage === 2) stage2 += 1;
      else if (r.current_stage === 3) stage3 += 1;
      else if (r.current_stage === 4) stage4 += 1;
    }
    const overdueTenants = overdueBookings.size;

    // Rent-domain counts for the header cards. Room occupancy lives on the
    // property overview, not here — it can't be derived from bookings without
    // producing impossible ratios like "occupied 14 / 12". Reminders-off
    // (owner-collected) units are excluded from both the count and the ratio —
    // we don't track their payment, so "On time 3 / 5" for 3 tracked + 2
    // untracked would be misleading.
    const trackedBookings = scopedBookings.filter(
      (r) => r.period_kind === "current" && r.reminders_enabled
    );
    const tenants = trackedBookings.length;
    const onTime = trackedBookings.filter(
      (r) => r.payment_status === "paid"
    ).length;

    return {
      tenants,
      onTime,
      overdueTenants,
      overdueTotalEur,
      overdueFeesEur,
      stage1,
      stage2,
      stage3,
      stage4,
    };
  }, [scopedRows, filteredSorted]);

  const handleExport = async () => {
    try {
      const rowsToExport = filteredSorted;
      const now = new Date();
      const stamp = now.toISOString().slice(0, 10);

      // Excel (.xlsx)
      const excelRows = rowsToExport.map((r) => {
        const totalOwed = r.monthly_rent + r.accumulated_fees_eur;
        const due =
          r.period_due_date || r.rent_due_date != null ? rowPeriodDue(r) : null;
        return {
          [t("owner.reminders.export.tenant", "Tenant")]: r.tenant_name ?? "",
          [t("owner.reminders.export.email", "Email")]: r.tenant_email ?? "",
          [t("owner.reminders.export.room", "Room")]: r.room_title ?? "",
          [t("owner.reminders.export.property", "Property")]:
            r.property_title ?? "",
          [t("owner.reminders.export.city", "City")]: r.property_city ?? "",
          [t("owner.reminders.export.status", "Status")]:
            r.payment_status ?? "",
          [t("owner.reminders.export.stage", "Stage")]: r.current_stage ?? 0,
          [t("owner.reminders.export.daysOverdue", "Days overdue")]:
            r.days_overdue ?? 0,
          [t("owner.reminders.export.dueDate", "Due date")]: due
            ? formatDateDe(due)
            : "",
          [t("owner.reminders.export.rent", "Rent")]: Number(
            r.monthly_rent ?? 0
          ),
          [t("owner.reminders.export.fees", "Fees")]: Number(
            r.accumulated_fees_eur ?? 0
          ),
          [t("owner.reminders.export.totalOwed", "Total owed")]:
            Number(totalOwed),
        };
      });
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelRows);
      XLSX.utils.book_append_sheet(
        wb,
        ws,
        t("owner.reminders.export.sheetName", "Reminders")
      );
      const xlsxArray = XLSX.write(wb, { type: "array", bookType: "xlsx" });
      downloadBlob(
        `payments-reminders_${stamp}.xlsx`,
        new Blob([xlsxArray], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        })
      );

      // PDF
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "pt",
        format: "a4",
      });
      doc.setFontSize(14);
      doc.text(
        t("owner.reminders.export.pdfTitle", "Payments & Reminders — Export"),
        40,
        40
      );
      doc.setFontSize(10);
      doc.text(
        `${t("owner.reminders.export.generatedAtPrefix", "Generated:")} ${now.toLocaleString(language === "de" ? "de-DE" : "en-GB")}`,
        40,
        58
      );

      const pdfBody = rowsToExport.map((r) => {
        const totalOwed = r.monthly_rent + r.accumulated_fees_eur;
        const due =
          r.period_due_date || r.rent_due_date != null ? rowPeriodDue(r) : null;
        return [
          r.tenant_name ??
            `${t("owner.reminders.bookingHashPrefix", "Booking")} #${r.booking_id}`,
          r.room_title ?? "—",
          r.property_title ?? "—",
          String(r.current_stage ?? 0),
          String(r.days_overdue ?? 0),
          due ? formatDateDe(due) : "—",
          formatEur(totalOwed),
        ];
      });

      autoTable(doc, {
        startY: 80,
        head: [
          [
            t("owner.reminders.export.tenant", "Tenant"),
            t("owner.reminders.export.room", "Room"),
            t("owner.reminders.export.property", "Property"),
            t("owner.reminders.export.stage", "Stage"),
            t("owner.reminders.export.daysOverdue", "Days overdue"),
            t("owner.reminders.export.dueDate", "Due"),
            t("owner.reminders.export.totalOwed", "Owed"),
          ],
        ],
        body: pdfBody,
        styles: { fontSize: 9, cellPadding: 6 },
        headStyles: { fillColor: [248, 250, 252], textColor: [71, 85, 105] },
        columnStyles: {
          6: { halign: "right" },
        },
        didDrawPage: () => {
          doc.setFontSize(9);
          doc.text(
            `${t("owner.reminders.export.rowsCountPrefix", "Rows:")} ${rowsToExport.length}`,
            doc.internal.pageSize.getWidth() - 120,
            58
          );
        },
      });
      doc.save(`payments-reminders_${stamp}.pdf`);

      toast.success(
        t(
          "owner.reminders.toast.exportDownloaded",
          "Export downloaded (Excel + PDF)"
        )
      );
    } catch (e) {
      toast.error(
        e instanceof Error
          ? e.message
          : t("owner.reminders.toast.exportFailed", "Export failed")
      );
    }
  };

  // ── Email preview ──
  const draftPreview = useMemo(() => {
    if (!draftFor) return null;
    const stage = (
      draftFor.current_stage > 0 ? draftFor.current_stage : 1
    ) as ReminderStage;
    const today = new Date();
    const periodDue = rowPeriodDue(draftFor, today);
    // Per-property Mahngebühr (COL-308) — the preview must show the same fee
    // the edge function renders into the sent email.
    const feePerStage = draftFor.fee_per_stage_eur;
    const feeCount =
      stage === 1
        ? 0
        : stage === 2
          ? 1
          : stage === 3
            ? 2
            : Math.max(
                3,
                feePerStage > 0
                  ? Math.round(draftFor.booking_fees_eur / feePerStage) + 1
                  : 3
              );
    const stage3Deadline = new Date(today);
    stage3Deadline.setUTCDate(stage3Deadline.getUTCDate() + 2);
    const rendered = renderReminderEmail(stage, {
      firstName:
        (draftFor.tenant_name ?? "").trim().split(/\s+/)[0] || "Mieter*in",
      fullName: draftFor.tenant_name?.trim() || "Mieter*in",
      room: draftFor.room_title?.trim() || `Buchung #${draftFor.booking_id}`,
      month: monthLabelDe(periodDue),
      dueDate: formatDateDe(periodDue),
      rentEur: draftFor.monthly_rent,
      feeCount,
      rentDueDay: draftFor.rent_due_date ?? undefined,
      stage3DeadlineDate: formatDateDe(stage3Deadline),
      feePerStage,
    });
    return { ...rendered, stage, feeCount };
  }, [draftFor]);

  useEffect(() => {
    if (draftPreview) setDraftSubject(draftPreview.subject);
  }, [draftPreview]);

  useEffect(() => {
    if (!draftPreview) return;
    setDraftHtml(draftPreview.html);
    setDraftText(draftPreview.text);
  }, [draftPreview]);

  // ── Mutations ──
  const sendReminder = useMutation({
    mutationFn: async () => {
      if (!draftFor || !draftPreview)
        throw new Error(
          t("owner.reminders.toast.noDraftSelected", "No draft selected")
        );
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token ?? "";
      const { data, error } = await supabase.functions.invoke(
        "send-rent-reminder",
        {
          body: {
            bookingId: draftFor.booking_id,
            stage: draftPreview.stage,
            // Past-month row: the edge function validates the open ledger
            // row and renders that month (COL-435).
            periodDueDate: draftFor.period_due_date ?? undefined,
            subjectOverride: draftSubject || draftPreview.subject,
            htmlOverride: draftHtml || draftPreview.html,
            textOverride: draftText || draftPreview.text,
            // The edge function honours German per spec; the lang flag is
            // logged on `payments_activity` for the timeline display.
            email_lang: draftLang,
          },
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }
      );
      if (error)
        // supabase-js collapses a non-2xx into the fixed string "Edge Function
        // returned a non-2xx status code" and leaves the real reason
        // ("Admin access required", "Stage mismatch — …") unread on error.context.
        throw new Error(
          await edgeFunctionErrorMessage(
            error,
            t(
              "owner.reminders.toast.couldNotSendReminder",
              "Could not send reminder"
            )
          )
        );
      return data;
    },
    onSuccess: () => {
      toast.success(t("owner.reminders.toast.reminderSent", "Reminder sent"));
      setDraftFor(null);
      queryClient.invalidateQueries({ queryKey: ["admin-reminders"] });
      queryClient.invalidateQueries({
        queryKey: [PROPERTY_REMINDER_STATS_KEY],
      });
    },
    onError: (e) =>
      toast.error(
        e instanceof Error
          ? e.message
          : t(
              "owner.reminders.toast.couldNotSendReminder",
              "Could not send reminder"
            )
      ),
  });

  const saveDraftActivity = useMutation({
    mutationFn: async () => {
      if (!draftFor || !draftPreview)
        throw new Error(
          t("owner.reminders.toast.noDraftSelected", "No draft selected")
        );
      await supabase.from("payments_activity").insert({
        booking_id: draftFor.booking_id,
        event_type: "reminder_draft_saved",
        stage: draftPreview.stage,
        amount_eur: null,
        email_lang: draftLang,
        note: `${draftSubject || draftPreview.subject}\n\n${draftText || draftPreview.text}`.trim(),
        email_to: draftFor.tenant_email ?? null,
        email_subject: draftSubject || draftPreview.subject,
        email_html: draftHtml || draftPreview.html,
        email_text: draftText || draftPreview.text,
        created_by: user?.id ?? null,
      });
    },
    onSuccess: () => {
      toast.success(
        t("owner.reminders.toast.draftSaved", "Draft saved to timeline")
      );
      queryClient.invalidateQueries({
        queryKey: ["admin-reminders", "activity", draftFor?.booking_id],
      });
    },
    onError: (e) =>
      toast.error(
        e instanceof Error
          ? e.message
          : t("owner.reminders.toast.couldNotSaveDraft", "Could not save draft")
      ),
  });

  const markPaid = useMutation({
    mutationFn: async () => {
      if (!markPaidFor)
        throw new Error(
          t("owner.reminders.toast.noBookingSelected", "No booking selected")
        );
      const totalOwed =
        markPaidFor.monthly_rent + markPaidFor.accumulated_fees_eur;
      const nowIso = new Date().toISOString();
      if (markPaidFor.period_kind === "past") {
        // A past month: settle THAT ledger row through the same proof-gated
        // pathway and log it. The booking's payment columns describe the
        // current period and stay untouched here; the payments→bookings sync
        // trigger recomputes rent_paid_through from the ledger.
        const { error: ledgerErr } = await recordManualRentPaidInLedger({
          bookingId: markPaidFor.booking_id,
          tenantUserId: markPaidFor.user_id,
          rentDueDay: markPaidFor.rent_due_date,
          monthlyRentEur: markPaidFor.monthly_rent,
          adminUserId: user?.id ?? null,
          periodDueDate: markPaidFor.period_due_date,
          // The row already exists (it's why this month showed as open) —
          // settle it directly so a manual/shell booking with no
          // `user_id` still gets its ledger row marked paid instead of
          // this silently no-op-ing (COL-435).
          paymentId: markPaidFor.ledger_payment_id,
        });
        if (ledgerErr) throw ledgerErr;
        if (markPaidFor.accumulated_fees_eur > 0) {
          // This row carried the booking's Mahngebühren (no overdue current
          // row); the dialog told the admin fees reset on confirmation.
          const { error: feeErr } = await supabase
            .from("bookings")
            .update({ accumulated_fees_eur: 0 })
            .eq("id", markPaidFor.booking_id);
          if (feeErr) throw feeErr;
        }
        await supabase.from("payments_activity").insert({
          booking_id: markPaidFor.booking_id,
          event_type: "marked_paid",
          amount_eur: totalOwed,
          stage: markPaidFor.current_stage || null,
          note: `Past month ${markPaidFor.period_due_date?.slice(0, 7) ?? ""} marked paid by admin.`,
          created_by: user?.id ?? null,
        });
        return;
      }
      // Ledger first (COL-227): record the payment in `payments` through the
      // proof-gated pathway so PaymentsHub / tenant profile / Dashboard /
      // analytics agree with the reminders view. Failing here aborts before
      // the booking columns are touched — no silent drift.
      const { error: ledgerErr } = await recordManualRentPaidInLedger({
        bookingId: markPaidFor.booking_id,
        tenantUserId: markPaidFor.user_id,
        rentDueDay: markPaidFor.rent_due_date,
        monthlyRentEur: markPaidFor.monthly_rent,
        adminUserId: user?.id ?? null,
      });
      if (ledgerErr) throw ledgerErr;
      // Gap-aware, mirroring recompute_booking_rent_coverage (COL-435): only
      // reset the dunning chain/fee pool when nothing else is outstanding.
      // Zeroing them while a past month stays open would regress its
      // `nextStageInChain` back to stage 1 and wipe the fees that
      // `placeBookingFees` is about to shift onto that oldest past row.
      const hasOpenPastMonth = rows.some(
        (r) =>
          r.booking_id === markPaidFor.booking_id && r.period_kind === "past"
      );
      const { error } = await supabase
        .from("bookings")
        .update({
          payment_status: "paid",
          last_payment_received_at: nowIso,
          ...(hasOpenPastMonth
            ? {}
            : { reminder_stage: 0, accumulated_fees_eur: 0 }),
        })
        .eq("id", markPaidFor.booking_id);
      if (error) throw error;
      await supabase.from("payments_activity").insert({
        booking_id: markPaidFor.booking_id,
        event_type: "marked_paid",
        amount_eur: totalOwed,
        stage: markPaidFor.current_stage || null,
        created_by: user?.id ?? null,
      });
    },
    onSuccess: () => {
      toast.success(t("owner.reminders.toast.markedAsPaid", "Marked as paid"));
      setMarkPaidFor(null);
      queryClient.invalidateQueries({ queryKey: ["admin-reminders"] });
      queryClient.invalidateQueries({
        queryKey: [PROPERTY_REMINDER_STATS_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [PROPERTY_RENT_PAID_STATS_KEY],
      });
      // A rent payment settling can flip a company_collects group's payout
      // readiness (COL-461) — the owners-list stat tile reads this too.
      invalidateOwnerCaches(queryClient);
    },
    onError: (e) =>
      toast.error(
        e instanceof Error
          ? e.message
          : t("owner.reminders.toast.couldNotMarkPaid", "Could not mark paid")
      ),
  });

  // ───────────────────────────────────────────────────────────────────────
  // Render
  // ───────────────────────────────────────────────────────────────────────

  const FilterChip = ({
    k,
    label,
    title,
  }: {
    k: FilterKey;
    label: string;
    title?: string;
  }) => {
    const active = filter === k;
    const count =
      k === "all_tenants"
        ? counts.all_tenants
        : k === "all_overdue"
          ? counts.all_overdue
          : k === "stage_1"
            ? counts.stage_1
            : k === "stage_2_3"
              ? counts.stage_2_3
              : k === "inkasso"
                ? counts.inkasso
                : k === "past_months"
                  ? counts.past_months
                  : counts.paid_this_month;
    return (
      <button
        type="button"
        title={title}
        onClick={() => setFilter(k)}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm transition-colors",
          active
            ? "bg-foreground text-background shadow-sm"
            : "bg-transparent text-muted-foreground hover:bg-muted/60"
        )}
      >
        {label}
        <span
          className={cn(
            "inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold tabular-nums",
            active
              ? "bg-background/20 text-background"
              : "bg-muted/70 text-foreground/70"
          )}
        >
          {count}
        </span>
      </button>
    );
  };

  const PropertyDropdown = () => {
    const selected = propertyFilter
      ? properties.find((p) => p.id === propertyFilter)
      : null;
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full h-9 gap-1.5"
          >
            <Building2 className="h-3.5 w-3.5 opacity-60" />
            {selected
              ? selected.title
              : t("owner.analytics.allProperties", "All properties")}
            <ChevronDown className="h-3.5 w-3.5 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="max-h-72 overflow-y-auto">
          <DropdownMenuItem onClick={() => setPropertyFilter(null)}>
            {t("owner.analytics.allProperties", "All properties")}
          </DropdownMenuItem>
          {properties.map((p) => (
            <DropdownMenuItem
              key={p.id}
              onClick={() => setPropertyFilter(p.id)}
            >
              {p.title}
              {p.city ? (
                <span className="ml-2 text-xs text-muted-foreground">
                  {p.city}
                </span>
              ) : null}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const SortDropdown = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full h-9 gap-1.5"
        >
          <ArrowDownUp className="h-3.5 w-3.5 opacity-60" />
          {sort === "days_overdue"
            ? t("owner.reminders.sort.daysOverdue", "Sort by days overdue")
            : sort === "amount"
              ? t("owner.reminders.sort.amount", "Sort by amount")
              : t("owner.reminders.sort.name", "Sort by name")}
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => setSort("days_overdue")}>
          {t("owner.reminders.sortOpt.daysOverdue", "Days overdue")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setSort("amount")}>
          {t("owner.reminders.sortOpt.amount", "Amount owed")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setSort("name")}>
          {t("owner.reminders.sortOpt.name", "Tenant name")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  // Inner content; AdminReminders wraps it in a Card unless `embedded`.
  const Body = (
    <>
      {/* Header */}
      {!embedded ? (
        <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-rose-400 to-rose-600 text-white flex items-center justify-center shadow-md shadow-rose-500/20 shrink-0">
              <MailWarning className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">
                {t("owner.reminders.pageTitle", "Payments & Reminders")}
              </h2>
              <div className="text-sm text-muted-foreground mt-0.5">
                {t(
                  "owner.reminders.pageSubtitle",
                  "Manual escalation. The system computes the stage; you draft and send."
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t(
                  "owner.reminders.searchPlaceholder",
                  "Search tenants, rooms, bookings…"
                )}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-9 w-[280px] rounded-full bg-background border-border/60 focus-visible:ring-rose-400/40"
              />
              {search ? (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground"
                  aria-label={t("owner.reminders.clearAria", "Clear")}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="rounded-xl h-9 gap-2"
              onClick={handleExport}
              disabled={filteredSorted.length === 0}
              title={t(
                "owner.reminders.exportTitle",
                "Downloads both Excel and PDF"
              )}
            >
              <Download className="h-4 w-4" />
              {t("owner.reminders.export", "Export")}
            </Button>
          </div>
        </div>
      ) : null}

      {/* Stats */}
      {!embedded ? (
        <div className="mb-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="rounded-2xl border border-sky-200/70 bg-sky-50/40 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                  {t("owner.reminders.statTenants", "Tenants tracked")}
                </div>
                <div className="text-2xl font-bold text-foreground tabular-nums mt-1">
                  {stats.tenants}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {propertyIdFilter != null
                    ? t(
                        "owner.reminders.statAcrossThisProperty",
                        "In this property"
                      )
                    : `${t("owner.reminders.statAcrossPrefix", "Across")} ${properties.length || 0} ${
                        (properties.length || 0) === 1
                          ? t("owner.reminders.propertySingular", "property")
                          : t("owner.reminders.propertyPlural", "properties")
                      }`}
                </div>
              </div>
              <div className="h-10 w-10 rounded-2xl bg-white/70 border border-sky-200 flex items-center justify-center text-sky-700">
                <UsersIcon className="h-5 w-5" />
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-emerald-200/70 bg-emerald-50/40 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                  {t("owner.reminders.statOnTime", "On time")}
                </div>
                <div className="text-2xl font-bold text-foreground tabular-nums mt-1">
                  {stats.onTime}
                  <span className="text-sm text-muted-foreground font-medium">
                    {" "}
                    / {stats.tenants}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {t(
                    "owner.reminders.statOnTimeHint",
                    "Paid for the current period"
                  )}
                </div>
              </div>
              <div className="h-10 w-10 rounded-2xl bg-white/70 border border-emerald-200 flex items-center justify-center text-emerald-700">
                <CheckCircle2 className="h-5 w-5" />
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-rose-200/70 bg-rose-50/40 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                  {t("owner.reminders.statOverdue", "Overdue")}
                </div>
                <div className="text-2xl font-bold text-rose-800 tabular-nums mt-1">
                  {formatEur(stats.overdueTotalEur)}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {stats.overdueTenants}{" "}
                  {t("owner.reminders.tenantsWord", "tenants")} ·{" "}
                  {formatEur(stats.overdueFeesEur)}{" "}
                  {t("owner.reminders.inFeesPhrase", "in fees")}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5"
                    title={reminderInternalLabel(1)}
                  >
                    {t("owner.reminders.stage1", "1st reminder")}{" "}
                    <span className="font-semibold tabular-nums">
                      {stats.stage1}
                    </span>
                  </span>
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-orange-50 text-orange-800 border border-orange-200 px-2 py-0.5"
                    title={reminderInternalLabel(2)}
                  >
                    {t("owner.reminders.stage2", "2nd reminder")}{" "}
                    <span className="font-semibold tabular-nums">
                      {stats.stage2}
                    </span>
                  </span>
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-orange-50 text-orange-900 border border-orange-300 px-2 py-0.5"
                    title={reminderInternalLabel(3)}
                  >
                    {t("owner.reminders.stage3", "3rd reminder")}{" "}
                    <span className="font-semibold tabular-nums">
                      {stats.stage3}
                    </span>
                  </span>
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-rose-50 text-rose-800 border border-rose-300 px-2 py-0.5"
                    title={reminderInternalLabel(4)}
                  >
                    {t("owner.reminders.filterInkasso", "Final notice")}{" "}
                    <span className="font-semibold tabular-nums">
                      {stats.stage4}
                    </span>
                  </span>
                </div>
              </div>
              <div className="h-10 w-10 rounded-2xl bg-white/70 border border-rose-200 flex items-center justify-center text-rose-700">
                <TriangleAlert className="h-5 w-5" />
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-border/60 bg-background p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                  {t("owner.reminders.statFiltered", "Filtered rows")}
                </div>
                <div className="text-2xl font-bold text-foreground tabular-nums mt-1">
                  {filteredSorted.length}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {t(
                    "owner.reminders.statFilteredHint",
                    "Export uses current filters + search"
                  )}
                </div>
              </div>
              <div className="h-10 w-10 rounded-2xl bg-muted flex items-center justify-center text-muted-foreground">
                <Download className="h-5 w-5" />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {summaryOnly ? null : (
        <>
          {/* Error banner */}
          {queryError ? (
            <div className="mb-5 rounded-2xl border border-rose-200 bg-rose-50 p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-rose-700 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-rose-900">
                    {t("owner.reminders.loadError", "Couldn't load reminders")}
                  </div>
                  <div className="text-xs text-rose-800 mt-1 break-words">
                    {queryError instanceof Error
                      ? queryError.message
                      : String(queryError)}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full h-8 text-xs border-rose-300 text-rose-700 hover:bg-rose-100"
                  onClick={() => refetch()}
                >
                  {t("owner.reminders.retry", "Retry")}
                </Button>
              </div>
            </div>
          ) : null}

          {/* Search + filter chips + dropdowns */}
          <div className="mb-4">
            {embedded ? (
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder={t(
                    "owner.reminders.searchPlaceholderEmbedded",
                    "Search by tenant, email, room, or booking id…"
                  )}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 h-10 rounded-xl bg-background border-border/60 focus-visible:ring-rose-400/40"
                />
                {search ? (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground"
                    aria-label={t("owner.reminders.clearAria", "Clear")}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                ) : null}
              </div>
            ) : null}
            <div className="flex flex-wrap items-center gap-2 justify-between">
              <div className="flex flex-wrap items-center gap-1">
                <FilterChip
                  k="all_tenants"
                  label={t("owner.reminders.filterAllTenants", "All")}
                />
                <FilterChip
                  k="all_overdue"
                  label={t("owner.reminders.filterAllOverdue", "Overdue")}
                />
                <FilterChip
                  k="stage_1"
                  label={t("owner.reminders.stage1", "1st reminder")}
                />
                <FilterChip
                  k="stage_2_3"
                  label={t("owner.reminders.filterStage23", "2nd–3rd reminder")}
                />
                <FilterChip
                  k="inkasso"
                  label={t("owner.reminders.filterInkasso", "Final notice")}
                />
                <FilterChip
                  k="paid_this_month"
                  label={t(
                    "owner.reminders.filterPaidMonth",
                    "Paid this month"
                  )}
                />
                <FilterChip
                  k="past_months"
                  label={t("owner.reminders.filterPastMonths", "Past months")}
                  title={t(
                    "owner.reminders.filterPastMonthsTooltip",
                    "Outstanding payments from previous billing periods"
                  )}
                />
              </div>
              <div className="flex items-center gap-2">
                <SortDropdown />
                {propertyIdFilter == null ? <PropertyDropdown /> : null}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Table */}
      <div className="rounded-2xl border border-border/60 bg-background overflow-hidden">
        {/* Header row */}
        <div className="hidden lg:grid grid-cols-[2fr_1.5fr_1.4fr_1fr_1.2fr_auto] gap-4 px-5 py-3 border-b border-border/60 bg-muted/30 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
          <div>{t("owner.reminders.colTenant", "Tenant")}</div>
          <div>{t("owner.reminders.colRoomProperty", "Room · Property")}</div>
          <div>{t("owner.reminders.colStatus", "Status")}</div>
          <div>{t("owner.reminders.colDue", "Due")}</div>
          <div className="text-right">
            {t("owner.reminders.colOwed", "Owed")}
          </div>
          <div />
        </div>

        {isLoading ? (
          <div className="py-16 flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            {t("owner.reminders.loading", "Loading…")}
          </div>
        ) : filteredSorted.length === 0 ? (
          <div className="py-14 text-center">
            <div className="mx-auto h-12 w-12 rounded-2xl bg-muted flex items-center justify-center mb-3">
              <MailWarning className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="text-sm font-medium text-foreground">
              {scopedRows.length > 0
                ? t(
                    "owner.reminders.emptyFiltered",
                    "No bookings match your filters"
                  )
                : t(
                    "owner.reminders.emptyNoBookings",
                    "No bookings to track yet"
                  )}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {scopedRows.length > 0
                ? t(
                    "owner.reminders.emptyFilteredHint",
                    "Try clearing the search or switching the filter chip."
                  )
                : t(
                    "owner.reminders.emptyNoBookingsHint",
                    "Bookings will appear here once tenants are added."
                  )}
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-border/60">
            {pageRows.map((r) => {
              const totalOwed = r.monthly_rent + r.accumulated_fees_eur;
              const isPast = r.period_kind === "past";
              const moveOutDue =
                isPast || r.rent_due_date != null ? rowPeriodDue(r) : null;
              const draftLabel = reminderActionLabel(r.current_stage);
              const statusSub = reminderStatusLabel({
                reminders_enabled: r.reminders_enabled,
                paid: r.payment_status === "paid",
                days_overdue: r.days_overdue,
                stage: r.current_stage,
              }).sub;
              return (
                <li key={r.row_key}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setOpenRowKey(r.row_key)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ")
                        setOpenRowKey(r.row_key);
                    }}
                    className="w-full text-left grid grid-cols-1 lg:grid-cols-[2fr_1.5fr_1.4fr_1fr_1.2fr_auto] gap-4 px-5 py-4 hover:bg-muted/40 transition-colors"
                  >
                    {/* Tenant — the name deep-links to the tenant profile
                        (COL-198); the rest of the row opens the detail pane. */}
                    <Link
                      to={`/admin?tab=tenants&bookingId=${r.booking_id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-3 min-w-0 group/name"
                    >
                      <div className="h-9 w-9 shrink-0 rounded-full bg-rose-50 text-rose-700 flex items-center justify-center text-xs font-semibold">
                        {(r.tenant_name || r.tenant_email || "?")
                          .split(" ")
                          .filter(Boolean)
                          .slice(0, 2)
                          .map((p) => p[0]?.toUpperCase())
                          .join("") || "?"}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-foreground truncate group-hover/name:text-emerald-700 group-hover/name:underline transition-colors">
                          {r.tenant_name ||
                            `${t("owner.reminders.bookingHashPrefix", "Booking")} #${r.booking_id}`}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {r.tenant_email ??
                            `${t("owner.reminders.bookingHashPrefix", "Booking")} #${r.booking_id}`}
                        </div>
                      </div>
                    </Link>

                    {/* Room · Property */}
                    <div className="min-w-0">
                      <div className="text-sm text-foreground truncate">
                        {r.room_title || "—"}
                        {r.property_title ? (
                          <>
                            <span className="text-muted-foreground/60">
                              {" "}
                              ·{" "}
                            </span>
                            <span className="text-muted-foreground">
                              {r.property_title}
                            </span>
                          </>
                        ) : null}
                      </div>
                      {r.property_city ? (
                        <div className="text-xs text-muted-foreground truncate">
                          {r.property_city}
                        </div>
                      ) : null}
                    </div>

                    {/* Status */}
                    <div className="flex flex-col items-start justify-center gap-1">
                      <StatusPill row={r} />
                      {statusSub ? (
                        <span className="text-xs text-muted-foreground">
                          {statusSub}
                        </span>
                      ) : null}
                    </div>

                    {/* Due */}
                    <div className="min-w-0">
                      {r.payment_status === "paid" || !r.reminders_enabled ? (
                        <div className="text-sm text-muted-foreground">—</div>
                      ) : r.days_overdue > 0 ? (
                        <>
                          <div className="text-sm font-semibold text-foreground">
                            {r.days_overdue}{" "}
                            {r.days_overdue === 1
                              ? t("owner.reminders.dayWord", "day")
                              : t("owner.reminders.daysWord", "days")}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {t("owner.reminders.duePrefix", "Due")}{" "}
                            {moveOutDue
                              ? formatDueDate(moveOutDue, isPast, language)
                              : "—"}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="text-sm text-foreground">
                            {moveOutDue
                              ? `${t("owner.reminders.duePrefix", "Due")} ${formatDueDate(moveOutDue, isPast, language)}`
                              : "—"}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {t(
                              "owner.reminders.withinGracePeriod",
                              "Within grace period"
                            )}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Owed */}
                    <div className="text-right">
                      <div
                        className={cn(
                          "text-sm font-semibold tabular-nums",
                          r.payment_status === "paid" || !r.reminders_enabled
                            ? "text-muted-foreground/60"
                            : r.current_stage >= 3
                              ? "text-rose-700"
                              : "text-foreground"
                        )}
                      >
                        {r.payment_status === "paid" || !r.reminders_enabled
                          ? "—"
                          : formatEur(totalOwed)}
                      </div>
                      {r.payment_status !== "paid" && r.reminders_enabled ? (
                        <div className="text-[11px] text-muted-foreground">
                          {formatEur(r.monthly_rent)}{" "}
                          {t("owner.reminders.rentWord", "rent")}
                          {r.accumulated_fees_eur > 0 ? (
                            <>
                              {" "}
                              · {formatEur(r.accumulated_fees_eur)}{" "}
                              {t("owner.reminders.feesWord", "fees")}
                            </>
                          ) : (
                            <>
                              {" "}
                              · {t("owner.reminders.noFeesYet", "No fees yet")}
                            </>
                          )}
                        </div>
                      ) : null}
                      {/* COL-417: the headline above is what is still owed. This says why it
                          is lower than the month's rent — without it a part-paid row just
                          looks like the rent was edited. */}
                      {r.rent_received_eur > 0 ? (
                        <div className="text-[11px] font-semibold tabular-nums text-blue-700">
                          {formatEur(r.rent_received_eur)}{" "}
                          {t("owner.reminders.ofBilled", "of")}{" "}
                          {formatEur(r.rent_billed_eur)}{" "}
                          {t("owner.reminders.receivedWord", "received")}
                        </div>
                      ) : null}
                    </div>

                    {/* Action */}
                    <div className="flex items-center justify-end">
                      {!r.reminders_enabled ? (
                        <span className="text-xs text-muted-foreground">
                          {t("owner.reminders.notTracked", "Not tracked")}
                        </span>
                      ) : r.payment_status === "paid" ? (
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          {t("owner.reminders.upToDate", "Up to date")}
                        </Badge>
                      ) : readOnly ? null : (
                        <Button
                          type="button"
                          size="sm"
                          variant={
                            r.current_stage === 0 ? "outline" : "default"
                          }
                          className={cn(
                            "rounded-xl shrink-0",
                            r.current_stage === 0
                              ? ""
                              : "bg-emerald-700 hover:bg-emerald-800 text-white"
                          )}
                          disabled={r.current_stage === 0}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDraftFor(r);
                          }}
                        >
                          {draftLabel}
                        </Button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Pagination */}
      {filteredSorted.length > PAGE_SIZE ? (
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-muted-foreground">
            {t("owner.reminders.showingPrefix", "Showing")}{" "}
            <span className="font-medium text-foreground">
              {(safePage - 1) * PAGE_SIZE + 1}–
              {Math.min(safePage * PAGE_SIZE, filteredSorted.length)}
            </span>{" "}
            {t("owner.reminders.ofWord", "of")}{" "}
            <span className="font-medium text-foreground">
              {filteredSorted.length}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="rounded-xl h-8"
              disabled={safePage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              {t("owner.reminders.prev", "Prev")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-xl h-8"
              disabled={safePage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              {t("owner.reminders.next", "Next")}
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );

  return (
    <>
      {embedded ? (
        <div className="space-y-3">{Body}</div>
      ) : (
        <Card className="border-border/60 shadow-sm overflow-hidden">
          <CardContent className="p-5 sm:p-6">{Body}</CardContent>
        </Card>
      )}

      {/* ───────────── Tenant detail dialog ───────────── */}
      <TenantDetailDialog
        row={openTenant}
        activity={activity ?? []}
        readOnly={readOnly}
        onClose={() => setOpenRowKey(null)}
        onDraft={(r) => {
          setDraftFor(r);
        }}
        onMarkPaid={(r) => setMarkPaidFor(r)}
      />

      {/* ───────────── Email draft modal ───────────── */}
      <EmailDraftDialog
        row={draftFor}
        preview={draftPreview}
        subject={draftSubject}
        setSubject={setDraftSubject}
        html={draftHtml}
        setHtml={setDraftHtml}
        text={draftText}
        setText={setDraftText}
        lang={draftLang}
        setLang={setDraftLang}
        onClose={() => setDraftFor(null)}
        onSaveDraft={() => saveDraftActivity.mutate()}
        onSend={() => sendReminder.mutate()}
        sending={sendReminder.isPending}
      />

      {/* ───────────── Mark-as-paid dialog ───────────── */}
      <MarkPaidDialog
        row={markPaidFor}
        onClose={() => setMarkPaidFor(null)}
        onConfirm={() => markPaid.mutate()}
        confirming={markPaid.isPending}
      />
    </>
  );
};

// ───────────────────────────────────────────────────────────────────────────
// Tenant detail dialog (slide-over)
// ───────────────────────────────────────────────────────────────────────────

function TenantDetailDialog({
  row,
  activity,
  readOnly = false,
  onClose,
  onDraft,
  onMarkPaid,
}: {
  row: ReminderRow | null;
  activity: ActivityRow[];
  /** Owner-portal viewers see dunning state read-only — no send/mark-paid. */
  readOnly?: boolean;
  onClose: () => void;
  onDraft: (r: ReminderRow) => void;
  onMarkPaid: (r: ReminderRow) => void;
}) {
  const { t, language } = useLanguage();
  const open = row != null;
  const today = new Date();
  const periodDue = row ? rowPeriodDue(row, today) : today;
  const totalOwed = (row?.monthly_rent ?? 0) + (row?.accumulated_fees_eur ?? 0);

  // Stage tracker pip data — current stage is `current_stage`; "completed"
  // pips are the stages already sent (reminder_stage), unsent stages are
  // dimmed.
  const sentUpTo = row?.reminder_stage ?? 0;
  const currentStage = row?.current_stage ?? 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[920px] max-h-[92vh] p-0 overflow-hidden flex flex-col rounded-2xl">
        <DialogHeader className="p-5 sm:p-6 border-b border-border/60 shrink-0">
          <DialogTitle className="text-lg font-semibold flex items-center gap-2 flex-wrap">
            {row?.tenant_name ||
              `${t("owner.reminders.bookingHashPrefix", "Booking")} #${row?.booking_id}`}
            {row ? <StatusPill row={row} /> : null}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {row?.room_title || t("owner.reminders.roomWord", "Room")}
            {row?.property_title ? ` · ${row.property_title}` : ""}
            {" · "}
            {t("owner.reminders.bookingHashPrefix", "Booking")} #
            {row?.booking_id}
          </DialogDescription>
        </DialogHeader>

        {row ? (
          <div className="flex-1 min-h-0 overflow-y-auto p-5 sm:p-6 space-y-5">
            {/* Alert banner — only when overdue */}
            {currentStage > 0 ? (
              <AlertBanner row={row} />
            ) : row.payment_status === "paid" ? (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-semibold text-emerald-900">
                    {t("owner.reminders.upToDate", "Up to date")}
                  </div>
                  <div className="text-xs text-emerald-800">
                    {row.last_payment_received_at ? (
                      <>
                        {t(
                          "owner.reminders.lastPaymentReceived",
                          "Last payment received"
                        )}{" "}
                        {formatDateNumeric(
                          row.last_payment_received_at,
                          language
                        )}
                        .
                      </>
                    ) : (
                      t(
                        "owner.reminders.noOutstandingRent",
                        "No outstanding rent."
                      )
                    )}
                  </div>
                </div>
              </div>
            ) : null}

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
              {/* Left: stage tracker + activity */}
              <div className="space-y-5">
                <div className="rounded-2xl border border-border/60 bg-background p-4 sm:p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="text-sm font-semibold text-foreground">
                        {t("owner.reminders.reminderStage", "Reminder stage")}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {t(
                          "owner.reminders.manualEscalationHint",
                          "Manual escalation — admin reviews and dispatches each stage."
                        )}
                      </div>
                    </div>
                  </div>
                  <StageTracker
                    sentUpTo={sentUpTo}
                    currentStage={currentStage}
                    feePerStage={row.fee_per_stage_eur}
                  />
                </div>

                <div className="rounded-2xl border border-border/60 bg-background p-4 sm:p-5">
                  <div className="text-sm font-semibold text-foreground">
                    {t("owner.reminders.activity", "Activity")}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 mb-4">
                    {t(
                      "owner.reminders.activityHint",
                      "Payments received, reminders sent, status changes."
                    )}
                  </div>
                  <ActivityTimeline
                    rows={activity}
                    currentStage={currentStage}
                    feePerStage={row.fee_per_stage_eur}
                  />
                </div>
              </div>

              {/* Right rail: outstanding card */}
              <div className="space-y-3">
                <div className="rounded-2xl border border-border/60 bg-background p-4 sm:p-5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-semibold text-foreground">
                      {t("owner.reminders.outstanding", "Outstanding")}
                    </div>
                    {currentStage > 0 ? (
                      <Badge className="bg-orange-50 text-orange-800 border-orange-200">
                        {`${t("owner.reminders.stagePrefix", "Stage")} ${currentStage} ${t("owner.reminders.activeSuffix", "active")}`}
                      </Badge>
                    ) : null}
                  </div>
                  <div className="text-3xl font-bold text-foreground tabular-nums leading-tight mb-3">
                    {formatEur(totalOwed)}
                  </div>
                  <div className="border-t border-border/60 pt-3 space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">
                        {t("owner.reminders.rentWord", "Rent")} ·{" "}
                        {monthLabelDe(periodDue)}
                      </span>
                      <span className="font-medium tabular-nums">
                        {formatEur(row.monthly_rent)}
                      </span>
                    </div>
                    {row.accumulated_fees_eur > 0 ? (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">
                          {t("owner.reminders.mahngebuehren", "Mahngebühren")}
                        </span>
                        <span className="font-medium tabular-nums text-rose-700">
                          +{formatEur(row.accumulated_fees_eur)}
                        </span>
                      </div>
                    ) : null}
                    {currentStage > sentUpTo && currentStage > 0 ? (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">
                          {`${t("owner.reminders.stagePrefix", "Stage")} ${currentStage} ${t("owner.reminders.feeOnSendSuffix", "fee (on send)")}`}
                        </span>
                        <span className="font-medium tabular-nums text-rose-700">
                          {currentStage === 1
                            ? "—"
                            : `+${formatEur(row.fee_per_stage_eur)}`}
                        </span>
                      </div>
                    ) : null}
                    <div className="flex items-center justify-between border-t border-border/60 pt-2">
                      <span className="font-semibold">
                        {t("owner.reminders.totalOwed", "Total owed")}
                      </span>
                      <span className="font-bold tabular-nums">
                        {formatEur(totalOwed)}
                      </span>
                    </div>
                  </div>
                  {row.payment_status !== "paid" && !readOnly ? (
                    <div className="mt-4 space-y-2">
                      <Button
                        type="button"
                        className="w-full rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white"
                        disabled={currentStage === 0}
                        onClick={() => onDraft(row)}
                      >
                        <Send className="h-4 w-4 mr-1.5" />
                        {currentStage === 0
                          ? t(
                              "owner.reminders.noReminderDueYet",
                              "No reminder due yet"
                            )
                          : `${t("owner.reminders.draftPrefix", "Draft")} ${
                              currentStage === 1
                                ? t(
                                    "owner.reminders.stage.friendly",
                                    "Friendly"
                                  )
                                : currentStage === 2
                                  ? "1. Mahnung"
                                  : currentStage === 3
                                    ? "2. Mahnung"
                                    : t(
                                        "owner.reminders.inkassoReferralWord",
                                        "Inkasso referral"
                                      )
                            }`}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full rounded-xl"
                        onClick={() => onMarkPaid(row)}
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1.5" />
                        {t("owner.reminders.markAsPaid", "Mark as paid")}
                      </Button>
                    </div>
                  ) : null}
                  {currentStage > 0 && currentStage < 4 ? (
                    <div className="mt-3 rounded-lg bg-muted/40 px-3 py-2 text-[11px] text-muted-foreground leading-relaxed">
                      <span className="font-semibold text-foreground">
                        {t("owner.reminders.nextPrefix", "Next:")}
                      </span>{" "}
                      {currentStage === 1
                        ? `${t("owner.reminders.nextStage2", "1. Mahnung at Day 5+")} (+${feeLabel(row.fee_per_stage_eur)}).`
                        : currentStage === 2
                          ? `${t("owner.reminders.nextStage3", "2. Mahnung at Day 7+")} (+${feeLabel(row.fee_per_stage_eur)}).`
                          : t(
                              "owner.reminders.nextStage4",
                              "Inkasso referral at Day 9+. Stage 4 hands the case to the collection agency."
                            )}
                    </div>
                  ) : null}
                  {currentStage === 4 ? (
                    <div className="mt-3 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-[11px] text-rose-900 leading-relaxed">
                      <span className="font-semibold">
                        {t("owner.reminders.inkassoActive", "Inkasso active.")}
                      </span>{" "}
                      {`${t("owner.reminders.inkassoRepeatsPrefix", "Stage 4 repeats every 2 days, adding")} ${feeLabel(row.fee_per_stage_eur)} ${t("owner.reminders.inkassoRepeatsSuffix", "each send. Additional inkasso & court costs apply outside this system.")}`}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function AlertBanner({ row }: { row: ReminderRow }) {
  const { t } = useLanguage();
  const stage = row.current_stage as ReminderStage;
  const next =
    stage === 1
      ? t(
          "owner.reminders.alert.next1",
          "1. Mahnung becomes available at Day 5+"
        )
      : stage === 2
        ? t(
            "owner.reminders.alert.next2",
            "2. Mahnung becomes available at Day 7+"
          )
        : stage === 3
          ? t(
              "owner.reminders.alert.next3",
              "Inkasso referral becomes available at Day 9+"
            )
          : t(
              "owner.reminders.alert.next4",
              "Stage 4 repeats every 2 days while unpaid"
            );
  const headline =
    stage === 1
      ? `${row.days_overdue} ${t("owner.reminders.alert.overdueSuffix", "days overdue")} · ${t("owner.reminders.alert.headline1Suffix", "Friendly reminder due now")}`
      : stage === 2
        ? `${row.days_overdue} ${t("owner.reminders.alert.overdueSuffix", "days overdue")} · ${t("owner.reminders.alert.headline2Suffix", "1. Mahnung due now")}`
        : stage === 3
          ? `${row.days_overdue} ${t("owner.reminders.alert.overdueSuffix", "days overdue")} · ${t("owner.reminders.alert.headline3Suffix", "2. Mahnung due now")}`
          : `${row.days_overdue} ${t("owner.reminders.alert.overdueSuffix", "days overdue")} · ${t("owner.reminders.alert.headline4Suffix", "Inkasso referral due now")}`;
  const tone =
    stage === 1
      ? "border-amber-200 bg-amber-50 text-amber-900"
      : stage === 2 || stage === 3
        ? "border-orange-200 bg-orange-50 text-orange-900"
        : "border-rose-200 bg-rose-50 text-rose-900";
  return (
    <div className={cn("rounded-2xl border p-4 flex items-start gap-3", tone)}>
      <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
      <div>
        <div className="text-sm font-semibold">{headline}</div>
        <div className="text-xs opacity-90 mt-0.5">{next}.</div>
      </div>
    </div>
  );
}

function StageTracker({
  sentUpTo,
  currentStage,
  feePerStage,
}: {
  sentUpTo: number;
  currentStage: number;
  feePerStage: number;
}) {
  const { t } = useLanguage();
  const labels = [
    t("owner.reminders.stage.friendly", "Friendly"),
    "1. Mahnung",
    "2. Mahnung",
    "Inkasso",
  ];
  return (
    <div className="grid grid-cols-4 gap-2">
      {labels.map((label, i) => {
        const stage = (i + 1) as 1 | 2 | 3 | 4;
        const isSent = stage <= sentUpTo;
        const isCurrent = stage === currentStage && !isSent;
        const isFuture = !isSent && !isCurrent;
        return (
          <div
            key={label}
            className={cn(
              "rounded-xl border p-3 text-xs",
              isSent
                ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                : isCurrent
                  ? "border-orange-300 bg-orange-50 text-orange-900 ring-1 ring-orange-300"
                  : "border-border/60 bg-muted/30 text-muted-foreground"
            )}
          >
            <div className="flex items-center gap-1.5 mb-1">
              <span
                className={cn(
                  "h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0",
                  isSent
                    ? "bg-emerald-600 text-white"
                    : isCurrent
                      ? "bg-orange-600 text-white"
                      : "bg-background border border-border text-muted-foreground"
                )}
              >
                {isSent ? <CheckCircle2 className="h-3 w-3" /> : stage}
              </span>
              <span className="font-semibold truncate">{label}</span>
            </div>
            <div className="text-[11px] leading-tight">
              {isSent
                ? t("owner.reminders.stage.sent", "Sent")
                : isCurrent
                  ? t("owner.reminders.stage.dueNow", "Due now")
                  : isFuture
                    ? stage === 1
                      ? t("owner.reminders.stage.atDay3", "At Day 3+")
                      : stage === 2
                        ? t("owner.reminders.stage.atDay5", "At Day 5+")
                        : stage === 3
                          ? t("owner.reminders.stage.atDay7", "At Day 7+")
                          : t("owner.reminders.stage.atDay9", "At Day 9+")
                    : ""}
              {!isSent && !isCurrent && stage > 1 ? (
                <> · +{feeLabel(feePerStage)}</>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ActivityTimeline({
  rows,
  currentStage,
  feePerStage,
}: {
  rows: ActivityRow[];
  currentStage: number;
  feePerStage: number;
}) {
  const { t, language } = useLanguage();
  const items: Array<{
    key: string;
    title: string;
    sub: string;
    color: "emerald" | "orange" | "amber" | "rose" | "slate";
    pending?: boolean;
  }> = [];

  // Pending row at top: if currentStage > sentUpTo (admin needs to act).
  // We can't reach sentUpTo here without re-deriving; left as a no-op when
  // there's no activity history.
  if (currentStage > 0 && rows.length === 0) {
    items.push({
      key: "pending-top",
      title: `${t("owner.reminders.stagePrefix", "Stage")} ${currentStage} ${t("owner.reminders.activityLog.draftPendingSuffix", "reminder — draft pending")}`,
      sub: t(
        "owner.reminders.activityLog.awaitingAdminSend",
        "Awaiting admin send"
      ),
      color: "orange",
      pending: true,
    });
  }

  for (const r of rows) {
    const ts = new Date(r.created_at).toLocaleString(
      language === "de" ? "de-DE" : "en-GB",
      {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }
    );
    if (r.event_type === "reminder_sent") {
      const stageLabel =
        r.stage === 1
          ? t(
              "owner.reminders.activityLog.friendlyReminder",
              "Friendly reminder"
            )
          : r.stage === 2
            ? "1. Mahnung"
            : r.stage === 3
              ? "2. Mahnung"
              : t(
                  "owner.reminders.activityLog.inkassoReferral",
                  "Inkasso referral"
                );
      items.push({
        key: r.id,
        title: `${stageLabel} ${t("owner.reminders.activityLog.sentWithLangSuffix", "sent")} (${(r.email_lang ?? "de").toUpperCase()})`,
        sub: `${ts}${r.stage && r.stage > 1 ? ` · +${feeLabel(feePerStage)} Mahngebühr` : ""}`,
        color: r.stage === 4 ? "rose" : r.stage === 1 ? "amber" : "orange",
      });
    } else if (r.event_type === "marked_paid") {
      items.push({
        key: r.id,
        title: `${t("owner.reminders.activityLog.markedAsPaidPrefix", "Marked as paid")} · ${formatEur(Number(r.amount_eur ?? 0))}`,
        sub: ts,
        color: "emerald",
      });
    } else if (r.event_type === "payment_received") {
      items.push({
        key: r.id,
        title: `${t("owner.reminders.activityLog.paymentReceivedPrefix", "Payment received")} · ${formatEur(Number(r.amount_eur ?? 0))}`,
        sub: ts,
        color: "emerald",
      });
    } else if (r.event_type === "inkasso_referred") {
      items.push({
        key: r.id,
        title: t(
          "owner.reminders.activityLog.caseReferredToInkasso",
          "Case referred to Inkasso"
        ),
        sub: ts,
        color: "rose",
      });
    }
  }

  if (items.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-3">
        {t(
          "owner.reminders.activityLog.noActivityYet",
          "No activity yet. Reminder sends and payments will appear here."
        )}
      </div>
    );
  }

  const dotColor: Record<string, string> = {
    emerald: "bg-emerald-500",
    orange: "bg-orange-500",
    amber: "bg-amber-500",
    rose: "bg-rose-500",
    slate: "bg-slate-400",
  };

  return (
    <ol className="relative ml-1 border-l border-border/60 pl-5 space-y-4">
      {items.map((it) => (
        <li key={it.key} className="relative">
          <span
            className={cn(
              "absolute -left-[27px] top-1 h-3 w-3 rounded-full ring-4 ring-background",
              dotColor[it.color],
              it.pending && "animate-pulse"
            )}
          />
          <div className="text-sm font-medium text-foreground">{it.title}</div>
          <div className="text-xs text-muted-foreground">{it.sub}</div>
        </li>
      ))}
    </ol>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Email draft modal — matches the mockup with From/To/Subject layout
// ───────────────────────────────────────────────────────────────────────────

function EmailDraftDialog({
  row,
  preview,
  subject,
  setSubject,
  html,
  setHtml,
  text,
  setText,
  lang,
  setLang,
  onClose,
  onSaveDraft,
  onSend,
  sending,
}: {
  row: ReminderRow | null;
  preview: {
    subject: string;
    html: string;
    text: string;
    stage: ReminderStage;
    feeCount: number;
  } | null;
  subject: string;
  setSubject: (s: string) => void;
  html: string;
  setHtml: (s: string) => void;
  text: string;
  setText: (s: string) => void;
  lang: "de" | "en";
  setLang: (l: "de" | "en") => void;
  onClose: () => void;
  onSaveDraft: () => void;
  onSend: () => void;
  sending: boolean;
}) {
  const { t } = useLanguage();
  const open = row != null;
  const stageLabel = preview
    ? `${t("owner.reminders.draft.stagePrefix", "Stage")} ${preview.stage}`
    : t("owner.reminders.draft.stage", "Stage");
  const previewDoc = (html || preview?.html || "").replace(
    /<script[\s\S]*?<\/script>/gi,
    ""
  );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[820px] max-h-[92vh] p-0 overflow-hidden flex flex-col rounded-2xl">
        <DialogHeader className="p-5 sm:p-6 border-b border-border/60 shrink-0">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold flex items-center gap-2">
                <span className="h-9 w-9 rounded-xl bg-orange-100 text-orange-700 flex items-center justify-center shrink-0">
                  <Send className="h-4 w-4" />
                </span>
                {t("owner.reminders.draft.draftPrefix", "Draft")} {stageLabel} —{" "}
                {row?.tenant_name ??
                  `${t("owner.reminders.bookingHashPrefix", "Booking")} #${row?.booking_id}`}
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground mt-0.5">
                {`${t("owner.reminders.draft.stagePrefix", "Stage")} ${preview?.stage ?? "—"}`}{" "}
                ·{" "}
                {t(
                  "owner.reminders.draft.reviewingBeforeSend",
                  "Reviewing before send"
                )}{" "}
                · {t("owner.reminders.draft.fromLabel", "From")}{" "}
                <code className="px-1 rounded bg-muted text-[11px]">
                  rechnungen@coliviq.de
                </code>
              </DialogDescription>
            </div>
            {/* DE / EN toggle. Currently only DE is sent (templates
                module is German-only); the toggle is logged on the
                payments_activity row so the timeline shows it. */}
            <div className="inline-flex rounded-full border border-border/60 bg-muted/30 p-1 text-xs">
              <button
                type="button"
                onClick={() => setLang("de")}
                className={cn(
                  "rounded-full px-3 py-1 font-medium",
                  lang === "de"
                    ? "bg-background shadow-sm text-foreground"
                    : "text-muted-foreground"
                )}
              >
                🇩🇪 DE
              </button>
              <button
                type="button"
                onClick={() => setLang("en")}
                className={cn(
                  "rounded-full px-3 py-1 font-medium",
                  lang === "en"
                    ? "bg-background shadow-sm text-foreground"
                    : "text-muted-foreground"
                )}
                title={t(
                  "owner.reminders.draft.enPreviewInfoOnly",
                  "EN preview is informational only — only the German template is dispatched per coliviq policy."
                )}
              >
                🇬🇧 EN
              </button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto p-5 sm:p-6 space-y-4">
          {/* Email fields */}
          <div className="rounded-2xl border border-border/60 bg-muted/30 overflow-hidden">
            <div className="grid grid-cols-[80px_1fr] divide-y divide-border/60">
              <div className="px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">
                {t("owner.reminders.draft.from", "From")}
              </div>
              <div className="px-4 py-3 text-sm">
                coliviq Rechnungen{" "}
                <span className="text-muted-foreground">
                  &lt;rechnungen@coliviq.de&gt;
                </span>
              </div>
              <div className="px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium border-t border-border/60">
                {t("owner.reminders.draft.to", "To")}
              </div>
              <div className="px-4 py-3 text-sm border-t border-border/60">
                {row?.tenant_name ?? "—"}{" "}
                {row?.tenant_email ? (
                  <span className="text-muted-foreground">
                    &lt;{row.tenant_email}&gt;
                  </span>
                ) : null}
              </div>
              <div className="px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium border-t border-border/60">
                <Label htmlFor="reminder-subject">
                  {t("owner.reminders.draft.subject", "Subject")}
                </Label>
              </div>
              <div className="px-4 py-2 border-t border-border/60">
                <Input
                  id="reminder-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="h-9 rounded-lg bg-background border-border/60"
                />
              </div>
            </div>
          </div>

          {/* Preview */}
          {preview ? (
            <div className="rounded-2xl border border-border/60 overflow-hidden bg-white relative">
              {lang === "en" ? (
                <div className="absolute right-3 top-3 z-10 rounded-md bg-amber-100 text-amber-900 border border-amber-300 px-2 py-1 text-[11px] font-medium shadow-sm">
                  {t(
                    "owner.reminders.draft.enPreviewBadge",
                    "EN preview · German body will be sent"
                  )}
                </div>
              ) : null}
              {/* Read-only preview — raw HTML must not be editable in the
                  draft flow (COL-198); the subject stays adjustable above. */}
              <iframe
                title={t(
                  "owner.reminders.draft.emailPreviewTitle",
                  "Email preview"
                )}
                className="w-full h-[720px] bg-white"
                sandbox="allow-same-origin"
                srcDoc={previewDoc}
              />
            </div>
          ) : null}

          {!row?.tenant_email ? (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
              <div className="text-xs text-amber-900">
                {t(
                  "owner.reminders.draft.noEmailOnFile",
                  "This tenant has no email address on file. Add one to their profile before sending."
                )}
              </div>
            </div>
          ) : null}
          <div className="text-[11px] text-muted-foreground text-center">
            {t(
              "owner.reminders.draft.archivedNotice",
              "A copy of this email will be archived and an activity log entry written."
            )}
          </div>
        </div>

        <DialogFooter className="p-4 sm:p-5 border-t border-border/60 shrink-0 bg-muted/10">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t("admin.action.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="rounded-xl"
            disabled={sending || !row?.tenant_email || !preview}
            onClick={onSaveDraft}
          >
            {t("owner.reminders.draft.saveDraft", "Save draft")}
          </Button>
          <Button
            type="button"
            className="rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white"
            disabled={sending || !row?.tenant_email || !preview}
            onClick={onSend}
          >
            {sending ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-1.5" />
            )}
            {t("owner.reminders.draft.sendEmail", "Send email")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Mark-as-paid confirmation dialog
// ───────────────────────────────────────────────────────────────────────────

function MarkPaidDialog({
  row,
  onClose,
  onConfirm,
  confirming,
}: {
  row: ReminderRow | null;
  onClose: () => void;
  onConfirm: () => void;
  confirming: boolean;
}) {
  const { t, language } = useLanguage();
  const open = row != null;
  const totalOwed = (row?.monthly_rent ?? 0) + (row?.accumulated_fees_eur ?? 0);
  const periodLabel =
    row && (row.period_due_date || row.rent_due_date != null)
      ? monthLabelDe(rowPeriodDue(row))
      : "";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[480px] rounded-2xl">
        <DialogHeader>
          <DialogTitle>
            {t("owner.reminders.markPaid.title", "Confirm payment received")}
          </DialogTitle>
          <DialogDescription>
            {row ? (
              <>
                {t(
                  "owner.reminders.markPaid.confirmPaymentOf",
                  "Confirm payment of"
                )}{" "}
                <strong className="text-foreground">
                  {formatEur(totalOwed)}
                </strong>{" "}
                {t("owner.reminders.markPaid.receivedFrom", "received from")}{" "}
                <strong className="text-foreground">
                  {row.tenant_name ||
                    row.tenant_email ||
                    `${t("owner.reminders.bookingHashPrefix", "Booking")} #${row.booking_id}`}
                </strong>
                {periodLabel ? (
                  <>
                    {" "}
                    {t("owner.reminders.markPaid.forPeriod", "for")}{" "}
                    {periodLabel}
                  </>
                ) : (
                  ""
                )}
                ?
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        {row && row.accumulated_fees_eur > 0 ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 space-y-1">
            <div>
              <strong>
                {t(
                  "owner.reminders.markPaid.includesFees",
                  "Includes Mahngebühren:"
                )}
              </strong>{" "}
              {formatEur(row.accumulated_fees_eur)}
            </div>
            <div>
              {t(
                "owner.reminders.markPaid.feesHint",
                "Confirm with the tenant whether the fees were collected. Fees are tracked separately from rent and reset to €0 upon confirmation."
              )}
            </div>
          </div>
        ) : null}

        {row?.inkasso_referred_at ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-900">
            <strong>
              {t(
                "owner.reminders.markPaid.inkassoReferenceRetained",
                "Inkasso reference retained."
              )}
            </strong>{" "}
            {t(
              "owner.reminders.markPaid.casePassedToCollectorOn",
              "Case was passed to the collector on"
            )}{" "}
            {formatDateNumeric(row.inkasso_referred_at, language)}.{" "}
            {t(
              "owner.reminders.markPaid.timestampStaysOnFile",
              "That timestamp stays on file for audit purposes."
            )}
          </div>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t("admin.action.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            className="rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white"
            disabled={confirming}
            onClick={onConfirm}
          >
            {confirming ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4 mr-1.5" />
            )}
            {t("owner.reminders.markPaid.confirmPayment", "Confirm payment")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AdminReminders;
